import { useState } from 'react';
import { formatPercent } from '../../utils/formatters';

// A labelled value chip shown above the bars.
function Stat({ label, value }) {
  return (
    <span className="inline-flex items-baseline gap-1.5">
      <span className="text-slate-400 dark:text-slate-500">{label}</span>
      <span className="font-semibold tabular-nums text-navy-800 dark:text-slate-100">
        {typeof value === 'number' ? value.toLocaleString() : value}
      </span>
    </span>
  );
}

// A ranked, PAGINATED bar list used in every tap-to-expand "drivers" panel (a product's
// resellers, or a reseller's products). Bars are ranked by value with a fixed page size
// and a Prev/Next pager (same style as the Table footer), so long lists page instead of
// truncating to "+N more". Bar widths are relative to the global max so lengths stay
// comparable across pages. `colorOf(item)` gives each bar its colour.
export function DriverBars({ items, countLabel, topLabel, colorOf, pageSize = 8 }) {
  const [page, setPage] = useState(0);
  const all = (items || []).filter((i) => i.value > 0).sort((a, b) => b.value - a.value);
  if (!all.length) return <p className="text-xs text-slate-400 dark:text-slate-500">No usage data.</p>;
  const total = all.reduce((s, i) => s + i.value, 0);
  const max = all[0].value;
  const totalPages = Math.ceil(all.length / pageSize);
  const cur = Math.min(page, totalPages - 1);
  const start = cur * pageSize;
  const view = all.slice(start, start + pageSize);
  return (
    <div>
      <div className="mb-2.5 flex flex-wrap gap-x-6 gap-y-1 text-xs">
        <Stat label={countLabel} value={all.length} />
        <Stat label="Total avg/day" value={total} />
        <Stat label={topLabel} value={all[0].name} />
      </div>
      <div className="space-y-1.5">
        {view.map((i) => (
          <div key={i.name} className="flex items-center gap-3">
            <span className="w-40 shrink-0 truncate text-xs text-slate-600 dark:text-slate-300" title={i.name}>{i.name}</span>
            <div className="h-2 flex-1 overflow-hidden rounded-full bg-slate-100 dark:bg-white/10">
              <div className="h-full rounded-full" style={{ width: `${Math.max((i.value / max) * 100, 3)}%`, background: colorOf(i) }} />
            </div>
            <span className="w-14 shrink-0 text-right text-xs font-semibold tabular-nums text-navy-800 dark:text-slate-100">{i.value.toLocaleString()}</span>
            <span className="w-14 shrink-0 text-right text-xs tabular-nums text-slate-400 dark:text-slate-500">{formatPercent((i.value / total) * 100)}</span>
          </div>
        ))}
      </div>
      {totalPages > 1 && (
        <div className="mt-2.5 flex items-center justify-between gap-3 text-[11px] text-slate-500 dark:text-slate-400">
          <span>Showing {start + 1}–{Math.min(start + pageSize, all.length)} of {all.length}</span>
          <div className="flex items-center gap-2">
            <button
              type="button" disabled={cur === 0} onClick={() => setPage(cur - 1)}
              className="rounded-md border border-slate-200 dark:border-white/15 bg-white/70 dark:bg-white/10 px-2 py-0.5 font-medium hover:bg-white dark:hover:bg-white/15 disabled:cursor-not-allowed disabled:opacity-40"
            >
              Prev
            </button>
            <span>Page {cur + 1} / {totalPages}</span>
            <button
              type="button" disabled={cur >= totalPages - 1} onClick={() => setPage(cur + 1)}
              className="rounded-md border border-slate-200 dark:border-white/15 bg-white/70 dark:bg-white/10 px-2 py-0.5 font-medium hover:bg-white dark:hover:bg-white/15 disabled:cursor-not-allowed disabled:opacity-40"
            >
              Next
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
